import * as turf from '@turf/turf'
import { LngLat, Map as MapboxMap, MapMouseEvent } from 'mapbox-gl'
import { action, computed, makeObservable, observable, when } from 'mobx'
import { MapRef, ViewState } from 'react-map-gl/mapbox'

import {
  GlobeViewMapType,
  IGeoJson2DGeographicCoordinates,
  IGeoJson2DGeographicCoordinatesInput,
  IGlobeViewStyle,
  IMutation,
  IProjectAddress,
  IProjectAddressInput,
  IRemoteTileset,
  ITileset,
} from '~/client/graph'
import { copyMapFrame } from '~/client/src/shared/components/MapBoxViewer/copyMapFrame'
import { IMapSettings } from '~/client/src/shared/components/MapBoxViewer/mapSettings'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { SCALED_IMAGE_CONFIG } from '~/client/src/shared/constants/scaledImage'
import {
  basemapStyleFor,
  nextProjection,
  Projection,
} from '~/client/src/shared/map/mapBasemapRule'
import {
  suspendMapAnimations,
  waitForMapFrame,
} from '~/client/src/shared/map/mapCaptureFrame'
import {
  DEFAULT_ZOOM_VALUE,
  isProjectMarkerOnlyZoom,
} from '~/client/src/shared/map/mapZoomRule'
import Sitemap from '~/client/src/shared/models/Sitemap'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import InitialState from '~/client/src/shared/stores/InitialState'
import { NORTH_HEADING } from '~/client/src/shared/utils/Address'

import Language from '../../localization/Language'
import Localization from '../../localization/LocalizationManager'
import Basemap from '../../models/Basemap'
import GlobeView from '../../models/GlobeView'
import SitemapItem from '../../models/SitemapItem'
import BasemapsStore from '../../stores/domain/Basemaps.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import SitemapItemsStore from '../../stores/domain/SitemapItems.store'
import TilesetsStore from '../../stores/domain/Tilesets.store'
import EventsStore from '../../stores/EventStore/Events.store'
import GlobeViewControlStore from '../../stores/GlobeViewControl.store'
import TilesetsControlStore from '../../stores/TilesetsControl.store'
import { MapFileOnGlobe } from '../SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import { resolveMapFilesOnGlobe } from './globeMapFiles'
import { getMapBoxUrl, mapboxFeatureToAddress } from './mapboxAddress'
import {
  DEFAULT_GLOBE_VIEW_STYLE,
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  resolveGlobeViewStyle,
  SHAPE_ARROW_LINE_LAYER,
  SHAPE_FILL_LAYER,
  SHAPE_LINE_LAYER,
} from './mapboxConstants'
import { enforceShapeLayerOrder } from './mapboxLayerOrder'
import {
  flyOutDuration,
  isSameCamera,
  isSameGlobeViewStyle,
  sitemapFitZoom,
} from './mapCamera'
import {
  IMapImageDensity,
  registerMissingMapImage,
  rerasterMapImages,
} from './mapImageRegistry'
import { isSatelliteStyle, mapStyleSettingsFrom } from './mapSettings'
import type { RemoteTilesetRecovery } from './remoteTilesetRecovery'
import { attachMapboxRemoteTilesetRecovery } from './remoteTilesetRecovery'
import {
  getTilesetFitCamera,
  waitForTilesetFitCamera,
} from './tilesetFitCamera'
import WorkflowMarkupsPreferences from './workflowMarkupsPreferences'

// Re-exported so existing importers keep their path; the values live in mapZoomRule.
export {
  DEFAULT_ZOOM_VALUE,
  isProjectMarkerOnlyZoom,
  MAX_ITEM_ZOOM_LEVEL,
  MAX_PITCH_LEVEL,
  MAX_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '~/client/src/shared/map/mapZoomRule'
export { ALLOWED_SITEMAP_ITEMS_WITHOUT_DATA_OBJECT } from './mapboxConstants'
export { getMapBoxUrl, mapboxFeatureToAddress }

export interface ISitemapWithBasemap {
  sitemap: Sitemap
  basemap: Basemap
}

const MIN_FLY_TO_ZOOM_LEVEL = 14
const FLYOUT_DURATION = 2000
// A tileset made visible by the eye toggle is framed in the same click, before its source has loaded.
// Wait for its bounds rather than framing on a value that would fly out to the region.
// Past it the user has moved on and stealing focus would be wrong.
const MAP_FOCUS_WAIT_MS = 5000
const THUMBNAIL_FRAME_WAIT_MS = 3000

enum MapboxLanguageCodes {
  en = 'en',
  fr = 'fr',
  es = 'es',
}

export default class MapBoxViewerStore {
  public clickCoords: LngLat = null
  @observable public projection: Projection = Projection.mercator
  @observable public isStyleLoaded: boolean = false
  // A mirror of the map's camera, not its source: the move events write it, and nothing feeds it
  // back as props. Before a map exists it doubles as the initial view state (see applyCamera).
  @observable public viewport: ViewState = {
    latitude: 0,
    longitude: 0,
    zoom: 0,
    bearing: NORTH_HEADING,
    pitch: 0,
    padding: null,
  }
  @observable public mapRef: MapRef = null
  private mapChangeCallbacks: Array<(map: MapboxMap | null) => void> = []
  // Set the first time the camera is framed or moved. Until then the mirror is still the {0,0}
  // initializer, and mapbox reads a missing initialViewState field as 0 — so the first framing
  // has to jump. Flying would animate across the planet from null island.
  private hasFramedCamera: boolean = false
  @observable public height: number = 0
  @observable public width: number = 0
  @observable public offsetX: number = 0
  @observable public offsetY: number = 0

  //map events props
  @observable public mapCursor = null
  // Clickable overlays (e.g. workflow pins) aren't in interactiveLayerIds, so
  // onMouseMove can't set the pointer — they report hover here; `cursor` reads it. Keyed by
  // source: each overlay probes on every mousemove, so a shared boolean would have the ones
  // finding nothing erase the one that did.
  private hoveredOverlaySources = observable.set<string>()
  @observable public markupEditorCursor: string = null
  // Drawing, or a markup selected: the edit layers draw above every pin and marker until released.
  @observable public isMarkupEditing: boolean = false
  @observable public selectedMarkerItemId: string = null
  @observable public hoveredShapeItemId: string = null
  @observable public hoveredMarkerItemId: string = null
  @observable public hoveredSitemapId: string = null

  @observable public globeViewStyle: IGlobeViewStyle = {
    ...DEFAULT_GLOBE_VIEW_STYLE,
  }
  // Workflow-markups visibility (Workflows page only). Visibility is a USER
  // preference (localStorage), overriding a project-level default
  // Who sees workflow markups, their label chips, and other workflows' markups. Its own
  // object because it is the one block here that never touches the map — no viewport, no
  // style, no mapRef, only the active user and project.
  public readonly markupPrefs: WorkflowMarkupsPreferences
  public tilesetsControlStore: TilesetsControlStore = null
  private remoteTilesetRecovery: RemoteTilesetRecovery | null = null

  public constructor(
    private readonly sitemapsStore: SitemapsStore,
    private readonly tilesetsStore: TilesetsStore,
    private readonly sitemapItemsStore: SitemapItemsStore,
    protected readonly locationAttributesStore: LocationAttributesStore,
    private readonly state: InitialState,
    public readonly globeViewControlStore: GlobeViewControlStore,
    public readonly basemapsStore: BasemapsStore,
    private readonly eventsStore?: EventsStore,
    public readonly selectedSitemapItem?: () => MapViewItemBase,
    public readonly selectSitemapItem?: (item: MapViewItemBase) => void,
  ) {
    this.markupPrefs = new WorkflowMarkupsPreferences(state, eventsStore)
    this.tilesetsControlStore = new TilesetsControlStore(
      tilesetsStore,
      state,
      globeViewControlStore,
    )
    makeObservable(this)
  }

  @action.bound
  public toggleProjection(): void {
    this.projection = nextProjection(this.projection)
  }

  @action.bound
  public toggleTerrain(): void {
    this.globeViewStyle.isTerrainEnabled = !this.globeViewStyle.isTerrainEnabled
  }

  @action.bound
  public toggle3DMode(): void {
    this.globeViewStyle.isThreeDEnabled = !this.globeViewStyle.isThreeDEnabled
  }

  @action.bound
  public toggleZoomScaling(): void {
    this.globeViewStyle.isZoomScalingEnabled =
      !this.globeViewStyle.isZoomScalingEnabled
  }

  @action.bound
  public toggleLabelOverlap(): void {
    this.globeViewStyle.isLabelOverlapEnabled =
      !this.globeViewStyle.isLabelOverlapEnabled
  }

  @action.bound
  public toggleWorkflowPinLabels(): void {
    this.globeViewStyle.shouldHideWorkflowPinLabels =
      !this.globeViewStyle.shouldHideWorkflowPinLabels
  }

  @action.bound
  public toggleMapStyle(): void {
    this.globeViewStyle.mapType =
      this.globeViewStyle.mapType === GlobeViewMapType.Street
        ? GlobeViewMapType.Satellite
        : GlobeViewMapType.Street

    this.globeViewStyle.isTrafficEnabled = false
    this.globeViewStyle.isThreeDEnabled = false
    this.globeViewStyle.isTerrainEnabled = false
  }

  @action.bound
  public toggleLabels(): void {
    this.globeViewStyle.shouldHideLabels = !this.globeViewStyle.shouldHideLabels
  }

  @action.bound
  public toggleTraffic(): void {
    this.globeViewStyle.isTrafficEnabled = !this.globeViewStyle.isTrafficEnabled
  }

  public get selectedGlobe(): GlobeView {
    return this.globeViewControlStore.selectedGlobeView
  }

  // A hovered marker wins over a hovered shape; at most one item highlights at a
  // time, so the GL layer can switch images by expression instead of re-emitting
  // the whole source on every hover.
  @computed
  public get highlightedItemId(): string {
    return this.hoveredMarkerItemId || this.hoveredShapeItemId
  }

  @action.bound
  public setInitStyle(): void {
    if (this.selectedGlobe?.style) {
      this.setStyleFormGlobe()
    } else {
      this.setDefaultStyle()
    }
  }

  @action.bound
  private setDefaultStyle() {
    this.globeViewStyle = { ...DEFAULT_GLOBE_VIEW_STYLE }
  }

  @computed
  public get isSatelliteActive(): boolean {
    return isSatelliteStyle(this.globeViewStyle)
  }

  public get mapSettings(): IMapSettings {
    return {
      ...mapStyleSettingsFrom(this.globeViewStyle),
      isShowWorkflowMarkupsEnabled: this.markupPrefs.showWorkflowMarkups,
      onToggleShowWorkflowMarkups: this.markupPrefs.supportsWorkflowMarkups
        ? this.markupPrefs.toggleWorkflowMarkups
        : undefined,
      isShowWorkflowMarkupLabelsEnabled:
        this.markupPrefs.isWorkflowMarkupLabelsVisible,
      onToggleShowWorkflowMarkupLabels: this.markupPrefs.supportsWorkflowMarkups
        ? this.markupPrefs.toggleWorkflowMarkupLabels
        : undefined,
      isWorkflowMarkupsProjectDefault:
        !this.state.activeProject?.areWorkflowMarkupsDisabledByDefault,
      onToggleWorkflowMarkupsProjectDefault: this.markupPrefs
        .isWorkflowMarkupsAdmin
        ? this.markupPrefs.toggleWorkflowMarkupsProjectDefault
        : undefined,
      isShowAdjacentMarkupsEnabled: this.markupPrefs.showAdjacentMarkups,
      onToggleShowAdjacentMarkups: this.markupPrefs
        .isAdjacentMarkupsToggleEnabled
        ? this.markupPrefs.toggleShowAdjacentMarkups
        : undefined,
      onMapTypeChange: this.toggleMapStyle,
      onToggle3D: this.toggle3DMode,
      onToggleTraffic: this.toggleTraffic,
      onToggleTerrain: this.toggleTerrain,
      onToggleZoomScaling: this.toggleZoomScaling,
      onToggleLabelOverlap: this.toggleLabelOverlap,
      onToggleWorkflowPinLabels: this.toggleWorkflowPinLabels,
      onToggleLabels: this.toggleLabels,
    }
  }

  public get mapStyle() {
    return basemapStyleFor(this.globeViewStyle.mapType)
  }

  // A boolean, not the raw zoom: `viewport` is replaced every camera frame, so everything derived
  // from this recomputes on the crossing instead.
  @computed
  public get shouldShowProjectMarker(): boolean {
    return isProjectMarkerOnlyZoom(this.viewport.zoom)
  }

  @computed
  public get globeCorners(): IGeoJson2DGeographicCoordinatesInput[] {
    return this.currentGlobeViewGeoCorners?.map(bound => {
      return {
        longitude: bound[0],
        latitude: bound[1],
      } as IGeoJson2DGeographicCoordinatesInput
    })
  }

  @computed
  public get currentGlobeViewGeoCorners(): number[][] {
    const map = this.mapRef?.getMap?.()

    if (!map) {
      return
    }
    const width: number = this.width || map.getCanvas().clientWidth
    const height: number = this.height || map.getCanvas().clientHeight
    const offsetX: number = this.offsetX || 0
    const offsetY: number = this.offsetY || 0

    const newPoint1 = map.unproject([offsetX, height + offsetY])
    const newPoint2 = map.unproject([offsetX + width, height + offsetY])
    const newPoint3 = map.unproject([offsetX + width, offsetY])
    const newPoint4 = map.unproject([offsetX, offsetY])

    return [
      [newPoint4.lng, newPoint4.lat],
      [newPoint3.lng, newPoint3.lat],
      [newPoint2.lng, newPoint2.lat],
      [newPoint1.lng, newPoint1.lat],
    ]
  }

  @action.bound
  public setDefaultMapMode(): void {
    this.setInitStyle()
  }

  @action.bound
  // isUserDriven defaults true: the programmatic seeds are framing the camera. Only mapbox's own
  // move events opt out, because a container resize arrives as one and must not count as framed.
  public setViewport(viewport: ViewState, isUserDriven = true): void {
    if (isUserDriven) {
      this.hasFramedCamera = true
    }
    this.viewport = viewport
  }

  // Put the camera on an exact pose, instantly. For the callers that used to write `viewport` and
  // let the controlled props follow: the map owns the camera now, and the move event mirrors the
  // result back. Fields left out keep whatever the camera is on.
  @action.bound
  public jumpToViewport(pose: Partial<ViewState>): void {
    const next = { ...this.viewport, ...pose }
    this.hasFramedCamera = true
    if (this.mapRef) {
      this.mapRef.jumpTo({
        center: [next.longitude, next.latitude],
        zoom: next.zoom,
        bearing: next.bearing,
        pitch: next.pitch,
      })
      return
    }
    this.viewport = next
  }

  @action.bound
  public onResize(): void {
    if (this.mapRef) {
      this.mapRef.resize()
    }
  }

  @action.bound
  public updateLanguage(): void {
    if (this.mapRef) {
      this.mapRef
        .getMap()
        .setLayoutProperty('country-label', 'text-field', [
          'get',
          `name_${this.language}`,
        ])
    }
  }

  @action.bound
  public setViewportFromItem(item: MapViewItemBase): void {
    this.setViewportFromSitemapItem(item?.sitemapItem)
  }

  @action.bound
  public selectViewportFromLocationAttributeId(
    locationAttributeId: string,
  ): void {
    const sitemapItem = this.sitemapItemsStore.list.find(
      sitemap => sitemap.assignedId === locationAttributeId,
    )
    if (sitemapItem) {
      this.setViewportFromSitemapItem(sitemapItem)
    }
  }

  @action.bound
  public setViewportFromSitemapItem(sitemapItem: SitemapItem): void {
    if (!sitemapItem?.coordinates) {
      return
    }
    this.setViewportFromCoordinates(sitemapItem.coordinates)
  }

  @action.bound
  public setViewportFromCoordinates(
    coords: {
      latitude: number
      longitude: number
    },
    offset?: [number, number],
    // The map being flown TO, while `viewport` still mirrors the one being left. Its own fields are
    // optional, so the fallback terminates here rather than reaching back to that stale mirror.
    pose?: { pitch?: number; bearing?: number },
  ): void {
    if (!coords) {
      return
    }
    const { latitude, longitude } = coords
    const animationTime = flyOutDuration(FLYOUT_DURATION, this.viewport, {
      latitude,
      longitude,
    })
    this.applyCamera(
      [longitude, latitude],
      Math.max(this.viewport.zoom, MIN_FLY_TO_ZOOM_LEVEL),
      pose ? (pose.pitch ?? 0) : this.viewport.pitch,
      pose ? (pose.bearing ?? NORTH_HEADING) : this.viewport.bearing,
      animationTime,
      offset,
    )
  }

  @action.bound
  public setViewportFromTileset(tile: ITileset): void {
    if (
      !tile ||
      !this.tilesetsControlStore.globeTilesets.some(t => t?.id === tile?.id)
    ) {
      return
    }
    void this.flyToTileset(tile)
  }

  private async flyToTileset(tile: ITileset): Promise<void> {
    const fit = await waitForTilesetFitCamera(() =>
      getTilesetFitCamera(this.mapRef?.getMap?.(), tile.id, {
        bearing: this.viewInfo.bearing,
        pitch: this.viewInfo.pitch,
      }),
    )
    if (!fit) {
      return
    }
    const flyOutDistance = turf.distance(fit.center, [
      this.viewport.longitude,
      this.viewport.latitude,
    ])
    const animationTime = FLYOUT_DURATION + flyOutDistance / 2
    this.applyCamera(
      fit.center,
      fit.zoom,
      this.viewInfo.pitch,
      this.viewInfo.bearing,
      animationTime,
    )
  }

  @action.bound
  public setViewportFromPlan(plan: Sitemap): void {
    if (
      !plan ||
      !plan.isReferenced ||
      !this.selectedGlobe?.sitemapsMap?.[plan.id]
    ) {
      return
    }
    const {
      center: { lat, lng },
      zoom,
      pitch,
      bearing,
    } = plan

    const flyOutDistance = turf.distance(
      [lng, lat],
      [this.viewport.longitude, this.viewport.latitude],
    )
    const animationTime = FLYOUT_DURATION + flyOutDistance / 2
    this.applyCamera([lng, lat], zoom, pitch, bearing, animationTime)
  }

  // remove mapbox info on unmount
  @action.bound
  public resetMapboxInfo(): void {
    this.hasFramedCamera = false
    this.viewport = {
      latitude: 0,
      longitude: 0,
      zoom: 0,
      bearing: NORTH_HEADING,
      pitch: 0,
      padding: null,
    }

    this.setInitStyle()
  }

  // zoom of the current sitemap mapbox calculated on bounds
  private getSitemapZoom(sitemap: Sitemap): number {
    if (!sitemap) {
      return null
    }
    return this.shouldShowProjectMarker
      ? sitemap.zoom
      : sitemapFitZoom(sitemap.bounds, this.width, this.height)
  }

  public get isResetDisabled(): boolean {
    if (!this.selectedGlobe) {
      return true
    }
    return (
      isSameCamera(this.viewport, {
        latitude: this.selectedGlobe.center.lat,
        longitude: this.selectedGlobe.center.lng,
        zoom: this.selectedGlobe.zoom,
        bearing: this.selectedGlobe.bearing,
        pitch: this.selectedGlobe.pitch,
      }) && isSameGlobeViewStyle(this.selectedGlobe.style, this.globeViewStyle)
    )
  }

  // minZoom: a caller that needs the framing not to sit below a floor states it here rather than
  // moving the camera a second time — the first move animates, so a follow-up read would be of a
  // mid-flight camera.
  @action.bound
  public setViewportFromAddress(
    sitemap?: Sitemap,
    isFullSizeImage?: boolean,
    minZoom?: number,
  ): void {
    const { projectAddress } = this.state

    const framedZoom = isFullSizeImage
      ? this.getSitemapZoom(sitemap)
      : (sitemap?.zoom ??
        this.viewInfo?.zoom ??
        projectAddress.zoom ??
        DEFAULT_ZOOM_VALUE)
    const zoom = minZoom == null ? framedZoom : Math.max(framedZoom, minZoom)
    const longitude =
      sitemap?.center?.lng ??
      this.viewInfo?.longitude ??
      projectAddress.center?.lng ??
      0
    const latitude =
      sitemap?.center?.lat ??
      this.viewInfo?.latitude ??
      projectAddress.center?.lat ??
      0

    const pitch =
      sitemap?.pitch ?? this.viewInfo?.pitch ?? projectAddress.pitch ?? 0
    const bearing =
      sitemap?.bearing ?? this.viewInfo?.bearing ?? projectAddress.bearing ?? 0
    this.applyCamera([longitude, latitude], zoom, pitch, bearing)
  }

  private setStyleFormGlobe(): void {
    if (!this.selectedGlobe) {
      return
    }
    const resolved = resolveGlobeViewStyle(this.selectedGlobe.style)
    this.globeViewStyle = resolved
  }

  // The numeric camera inputs. jumpTo, not the mirror: typing a coordinate has to move the map,
  // and the move event writes the mirror back. Latitude and longitude are not jumpTo options —
  // mapbox takes them together as `center`, so the other half comes off the mirror.
  @action.bound
  public setViewportProp(propValue: number, propName: string): void {
    // The numeric inputs hand over Number(event.target.value), so a half-typed "-" arrives as NaN
    // and mapbox's LngLat throws on it.
    if (!Number.isFinite(propValue)) {
      return
    }
    if (!this.mapRef) {
      this.viewport[propName] = propValue
      return
    }
    switch (propName) {
      case 'latitude':
        this.mapRef.jumpTo({ center: [this.viewport.longitude, propValue] })
        return
      case 'longitude':
        this.mapRef.jumpTo({ center: [propValue, this.viewport.latitude] })
        return
      default:
        this.mapRef.jumpTo({ [propName]: propValue })
    }
  }

  // Mapbox binds its keyboard handler to the canvas container, so arrow keys are
  // dead until the canvas takes focus — otherwise only on a map click. Waits for
  // the map: a pinless Precise Location field renders none until it expands.
  public focusMapCanvas(): void {
    when(
      () => !!this.mapRef,
      () => this.mapRef?.getMap()?.getCanvas()?.focus({ preventScroll: true }),
      { timeout: MAP_FOCUS_WAIT_MS, onError: () => undefined },
    )
  }

  // A recovery replaces the map instance (STX1-1068) and the consumers that need to know
  // sit outside its React tree. Notified from the one setter the handover goes through.
  public onMapChange(callback: (map: MapboxMap | null) => void): () => void {
    this.mapChangeCallbacks.push(callback)

    return () => {
      this.mapChangeCallbacks = this.mapChangeCallbacks.filter(
        cb => cb !== callback,
      )
    }
  }

  @action.bound
  public setRefForProjectMap(ref: MapRef | null): void {
    this.remoteTilesetRecovery?.dispose()
    this.remoteTilesetRecovery = null
    const prev = this.mapRef?.getMap()
    // react-map-gl destroys the map it hands back, so keeping the handle would pin a dead GL
    // instance for the store's lifetime.
    prev?.off('styleimagemissing', this.onStyleImageMissing)
    prev?.off('styledata', this.keepShapesAboveOverlays)
    prev?.off('idle', this.keepShapesAboveOverlays)
    this.mapRef = ref
    const map = ref?.getMap()
    if (map) {
      map.on('styleimagemissing', this.onStyleImageMissing)
      map.on('styledata', this.keepShapesAboveOverlays)
      map.on('idle', this.keepShapesAboveOverlays)
      this.remoteTilesetRecovery = attachMapboxRemoteTilesetRecovery(
        map,
        'mapbox-viewer',
      )
    }
    this.mapChangeCallbacks.forEach(cb => cb(map ?? null))
  }

  // Map File overlays re-add above the shape layers after a view switch. Move them back beneath the
  // lowest shape (not shapes to top, which would bury selected/editor layers).
  @action.bound
  // Not named after the function it calls: one `this.` in a future edit would recurse inside a
  // styledata handler.
  public keepShapesAboveOverlays(): void {
    if (!this.mapRef || !this.isStyleLoaded) {
      return
    }
    enforceShapeLayerOrder(this.mapRef.getMap(), this.isMarkupEditing)
  }

  // No style event follows the flag, so the order is re-applied here.
  @action.bound
  public setMarkupEditing(isEditing: boolean): void {
    if (this.isMarkupEditing === isEditing) {
      return
    }
    this.isMarkupEditing = isEditing
    this.keepShapesAboveOverlays()
  }

  @action.bound
  public setMarkupEditorCursor(cursor: string | null): void {
    this.markupEditorCursor = cursor
  }

  // How sharp this map rasterizes its pins and Location icons; set by a host that draws them
  // larger than their own size (the export preview).
  private imageDensity: IMapImageDensity | undefined

  public setImageDensity(density: IMapImageDensity): void {
    const previous = this.imageDensity
    this.imageDensity = density
    // Only the root style must be in: isStyleLoaded() also waits for imported basemaps, and the
    // icons drawn meanwhile would keep their old density.
    const map = this.mapRef?.getMap()
    const style = (map as unknown as { style?: { _loaded?: boolean } })?.style
    if (style?._loaded) rerasterMapImages(map, density, previous)
  }

  @action.bound
  private onStyleImageMissing(e: { id: string }): void {
    if (!this.mapRef) return
    registerMissingMapImage(this.mapRef.getMap(), e.id, this.imageDensity)
  }

  // Mirrors what MapCanvas reports, for the views gallery reading it from outside the map tree.
  @action.bound
  public setStyleLoaded(isLoaded: boolean): void {
    this.isStyleLoaded = isLoaded
    if (!isLoaded) {
      return
    }
  }

  @action.bound
  public removeRefs(): void {
    this.setRefForProjectMap(null)
  }

  @action.bound
  public onAddressChanged(projectAddress: IProjectAddressInput): void {
    this.saveAddress(projectAddress)
  }

  @computed
  public get mapFilesOnGlobe(): MapFileOnGlobe[] {
    return resolveMapFilesOnGlobe(this.selectedGlobe?.mapFiles, {
      getSitemap: id => this.sitemapsStore.byId.get(id),
      getBasemap: id => this.basemapsStore.byId.get(id),
      getTileset: id => this.state.activeTilesetsById.get(id),
      getRemoteTileset: id => this.state.remoteTilesets.get(id),
    })
  }

  @computed
  public get sitemapWithBasemapsOnGlobe(): ISitemapWithBasemap[] {
    return this.globePlans.map(s => ({
      sitemap: s,
      basemap: this.basemapsStore.byId.get(s.basemapId),
    }))
  }

  @computed
  public get sitemapsMap(): { [mapId: string]: ISitemapWithBasemap } {
    return this.sitemapWithBasemapsOnGlobe.reduce((map, sitemapWithBasemap) => {
      map[sitemapWithBasemap.sitemap.id] = sitemapWithBasemap
      return map
    }, {})
  }

  // sitemaps to show as additional on the 'GLOBE'
  @computed
  public get globePlans(): Sitemap[] {
    return this.selectedGlobe
      ? this.selectedGlobe.sitemapIds
          .filter(sitemapId => !!this.sitemapsStore.byId.get(sitemapId))
          .map(sitemapId => this.sitemapsStore.byId.get(sitemapId))
      : []
  }

  @computed
  public get globeRemoteTilesets(): IRemoteTileset[] {
    return this.selectedGlobe
      ? this.selectedGlobe.remoteTilesetIds
          .filter(id => !!this.state.remoteTilesets.get(id))
          .map(id => this.state.remoteTilesets.get(id))
      : []
  }

  public getItemCoordinates(
    x: number,
    y: number,
  ): IGeoJson2DGeographicCoordinates {
    if (!this.mapRef?.getMap?.()) {
      return null
    }
    const newPoint = this.mapRef?.getMap().unproject([x, y])

    return {
      longitude: newPoint.lng,
      latitude: newPoint.lat,
    }
  }

  @computed
  public get displayedGlobeViewItems(): MapViewItemBase[] {
    if (this.shouldShowProjectMarker) {
      return []
    }
    return this.globeViewControlStore.selectedGlobeViewItems.filter(
      i => i.isVisibleOnMap,
    )
  }

  public get allGlobeViewItems(): MapViewItemBase[] {
    return this.globeViewControlStore.selectedGlobeViewItems
  }

  @action.bound
  public async createMapImageDataUrl(): Promise<string> {
    const map = this.mapRef?.getMap?.()
    if (!map) {
      return null
    }

    const resume = suspendMapAnimations(map)
    let canvas: HTMLCanvasElement
    try {
      // Unlike a report shot, a thumbnail is better late than missing, so an unfinished frame is
      // still taken.
      await waitForMapFrame(map, THUMBNAIL_FRAME_WAIT_MS)
      // pixelRatio, not the frame's own density: a fixed 2x of the CSS box on every display, as
      // rasterizing the DOM at scale 2 produced. Reading the backing store 1:1 halves it at 1x.
      canvas = copyMapFrame(map, {
        pixelRatio: SCALED_IMAGE_CONFIG.pixelRatio,
      })
    } finally {
      resume()
    }
    if (!canvas) {
      return null
    }

    return canvas.toDataURL(
      SCALED_IMAGE_CONFIG.mimeType,
      SCALED_IMAGE_CONFIG.quality,
    )
  }

  @action.bound
  public onMapClick(e: MapMouseEvent): void {
    const selectedMapViewItem =
      this.selectedSitemapItem && this.selectedSitemapItem()
    let wasItemSelected = false
    if (this.selectedMarkerItemId) {
      this.selectedMarkerItemId = null
      return
    }
    if (e?.features?.length) {
      switch (e.features[0]?.layer?.id) {
        case SHAPE_FILL_LAYER:
        case SHAPE_LINE_LAYER:
          const itemId = e?.features[0]?.properties?.itemId
          if (itemId && itemId !== selectedMapViewItem?.id) {
            this.selectSitemapItem?.(
              this.displayedGlobeViewItems.find(
                i => i.id === e?.features[0]?.properties?.itemId,
              ),
            )
            wasItemSelected = true
          }
          break
        case ITEM_EDITOR_SHAPE_FILL_LAYER:
          break
      }
    }
    if (!wasItemSelected && selectedMapViewItem && !this.selectedMarkerItemId) {
      this.selectSitemapItem?.(null)
    }
    this.selectedMarkerItemId = null
  }

  @computed
  public get isOverlayPointerHovered(): boolean {
    return !!this.hoveredOverlaySources.size
  }

  // Argument order matches the pin layers' `onHoverChange`, and @action.bound keeps the identity
  // stable, so a surface passes this straight to the layer instead of wrapping it to satisfy
  // React.memo.
  @action.bound
  public setOverlayPointerHovered(hovered: boolean, source: string): void {
    if (hovered) {
      this.hoveredOverlaySources.add(source)
    } else {
      this.hoveredOverlaySources.delete(source)
    }
  }

  @action.bound
  public onMouseMove(e: MapMouseEvent): void {
    this.mapCursor = e?.features?.length ? 'pointer' : null

    const shapeFeature = e.features?.find(f =>
      [SHAPE_FILL_LAYER, SHAPE_LINE_LAYER, SHAPE_ARROW_LINE_LAYER].includes(
        f?.layer?.id,
      ),
    )

    if (shapeFeature) {
      this.hoveredShapeItemId = shapeFeature.properties?.itemId || null
    } else {
      this.hoveredShapeItemId = null
    }
  }

  public saveAddress(projectAddress: IProjectAddress): Promise<IMutation> {
    return new Promise((resolve, reject) => {
      const { id: projectAddressId } = this.state.projectAddress
      const { id: projectId } = this.state.activeProject
      this.eventsStore.dispatch(
        e.SAVE_PROJECT_ADDRESSES,
        [
          Object.assign(projectAddress, {
            id: projectAddressId,
            projectId,
          }),
        ],
        resolve,
        reject,
      )
    })
  }

  // The one way this store moves the camera. A live map is flown and reports back through `move`,
  // which is what keeps `viewport` a mirror rather than a second source of truth. With no map yet
  // there is nothing to fly, so the write lands on the mirror and MapBoxViewer mounts from it.
  @action.bound
  private applyCamera(
    center: [number, number],
    zoom: number,
    pitch?: number,
    bearing?: number,
    duration?: number,
    // A one-shot shift of where the target lands in the canvas, for a caller whose chrome covers
    // part of the map. Not `padding`, which mapbox keeps as camera state — onMove mirrors it back,
    // so it would inset every later framing too. Inert on the mirror branch: no canvas, no pixels.
    offset?: [number, number],
  ): void {
    const [longitude, latitude] = center
    const wasFramed = this.hasFramedCamera
    this.hasFramedCamera = true
    if (this.mapRef) {
      this.mapRef.flyTo({
        center,
        essential: true,
        zoom,
        pitch,
        bearing,
        // Never `undefined`: mapbox merges options over its defaults, so an explicit undefined
        // clobbers the [0, 0] default and Point.convert throws — killing the whole camera move.
        offset: offset ?? [0, 0],
        duration: wasFramed ? duration || FLYOUT_DURATION : 0,
      })
      return
    }
    this.viewport = {
      ...this.viewport,
      latitude,
      longitude,
      zoom: zoom ?? this.viewport.zoom,
      pitch: pitch ?? this.viewport.pitch,
      bearing: bearing ?? this.viewport.bearing,
    }
  }

  public get viewInfo(): ViewState {
    const globe = this.selectedGlobe
    const { projectAddress } = this.state

    if (globe) {
      if (!globe.id) {
        return {
          bearing: projectAddress.bearing,
          zoom: projectAddress.zoom,
          pitch: projectAddress.pitch,
          latitude: projectAddress?.center?.lat,
          longitude: projectAddress?.center?.lng,
          padding: null,
        }
      }
      return {
        bearing: globe.bearing,
        zoom: globe.zoom,
        pitch: globe.pitch,
        latitude: globe?.center?.lat,
        longitude: globe?.center?.lng,
        padding: null,
      }
    }

    return null
  }

  private get language(): string {
    switch (Localization.state.currentLanguage) {
      case Language.english:
      case Language.ukrainian:
        return MapboxLanguageCodes.en
      case Language.french:
        return MapboxLanguageCodes.fr
      case Language.spanish:
        return MapboxLanguageCodes.es
    }
  }
}
